import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { services } from "../services";

type StateLimit = {
  stateId: number;
  stateName: string;
  currentLimit: number | null;
  currentRegistrations: number;
};

type LimitsResponse = {
  states: StateLimit[];
  vleLimit: number | null;
};

export function OnboardingLimitSettingsContent() {
  const [states, setStates] = useState<StateLimit[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<number | "">("");
  const [stateLimitInput, setStateLimitInput] = useState<string>("");
  const [vleLimitInput, setVleLimitInput] = useState<string>("");
  const [vleCurrentLimit, setVleCurrentLimit] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingState, setIsSavingState] = useState(false);
  const [isSavingVle, setIsSavingVle] = useState(false);

  const selectedState = useMemo(
    () =>
      states.find((state) => Number(state.stateId) === Number(selectedStateId)),
    [states, selectedStateId],
  );

  const fetchLimits = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await services.requestSvc.get<LimitsResponse>(
        "/onboarding-limits",
        {
          withCredentials: true,
        },
      );
      setStates(data.states || []);
      setVleCurrentLimit(data.vleLimit ?? null);
      setVleLimitInput(
        data.vleLimit !== null && data.vleLimit !== undefined
          ? String(data.vleLimit)
          : "",
      );
    } catch (error) {
      console.error("Failed to fetch onboarding limits", error);
      toast.error("Failed to fetch onboarding limits.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLimits();
  }, [fetchLimits]);

  useEffect(() => {
    if (!selectedStateId) {
      setStateLimitInput("");
      return;
    }

    const state = states.find(
      (item) => Number(item.stateId) === Number(selectedStateId),
    );
    setStateLimitInput(
      state && state.currentLimit !== null && state.currentLimit !== undefined
        ? String(state.currentLimit)
        : "",
    );
  }, [selectedStateId, states]);

  const validateStateLimit = useCallback((value: string, state?: StateLimit) => {
    if (!state) return "Please select a state.";
    if (value.trim() === "") return "Please enter a limit.";
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return "Please enter a valid non-negative number.";
    }
    if (parsed < state.currentRegistrations) {
      return "You cannot update the limit lower than the current total registrations.";
    }
    return null;
  }, []);

  const validateVleLimit = useCallback(
    (value: string) => {
      if (value.trim() === "") return "Please enter a limit.";
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return "Please enter a valid non-negative number.";
      }
      if (vleCurrentLimit !== null && parsed < vleCurrentLimit) {
        return "You cannot update the limit lower than the current limit.";
      }
      return null;
    },
    [vleCurrentLimit],
  );

  const handleStateSubmit = async () => {
    const validationError = validateStateLimit(stateLimitInput, selectedState);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (!selectedState) return;

    setIsSavingState(true);
    try {
      await services.requestSvc.put(
        "/onboarding-limits/state",
        {
          stateId: selectedState.stateId,
          newLimit: Number(stateLimitInput),
        },
        { withCredentials: true },
      );
      await fetchLimits();
      toast.success("State limit updated successfully.");
    } catch (error: any) {
      toast.error(error?.message || "Failed to update state limit.");
    } finally {
      setIsSavingState(false);
    }
  };

  const handleVleSubmit = async () => {
    const validationError = validateVleLimit(vleLimitInput);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSavingVle(true);
    try {
      const updated = await services.requestSvc.put<{ currentLimit: number }>(
        "/onboarding-limits/vle",
        { newLimit: Number(vleLimitInput) },
        { withCredentials: true },
      );
      setVleCurrentLimit(updated.currentLimit);
      toast.success("VLE learner onboarding limit updated successfully.");
    } catch (error: any) {
      toast.error(error?.message || "Failed to update VLE limit.");
    } finally {
      setIsSavingVle(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">
          State Onboarding Limit
        </h2>
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading limits...</div>
        ) : (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select State
            </label>
            <select
              className="input-primary w-full mb-4"
              value={selectedStateId}
              onChange={(event) => {
                setSelectedStateId(
                  event.target.value ? Number(event.target.value) : "",
                );
              }}
            >
              <option value="">Choose a state</option>
              {states.map((state) => (
                <option key={state.stateId} value={state.stateId}>
                  {state.stateName}
                </option>
              ))}
            </select>

            {selectedState && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-sm text-gray-600">
                <div>
                  <div className="font-medium text-gray-700">Current Limit</div>
                  <div>
                    {selectedState.currentLimit !== null
                      ? selectedState.currentLimit
                      : "Not set"}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-gray-700">
                    Current Registrations
                  </div>
                  <div>{selectedState.currentRegistrations}</div>
                </div>
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Limit
            </label>
            <input
              type="number"
              min={0}
              className="input-primary w-full"
              value={stateLimitInput}
              onChange={(event) => {
                setStateLimitInput(event.target.value);
              }}
            />

            <button
              type="button"
              onClick={handleStateSubmit}
              className="btn-primary w-full mt-4"
              disabled={isSavingState || !selectedStateId}
            >
              {isSavingState ? "Updating..." : "Update State Limit"}
            </button>
          </>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">
          VLE Learner Onboarding Limit
        </h2>
        <div className="text-sm text-gray-600 mb-4">
          Current limit: {vleCurrentLimit ?? "Not set"}
        </div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          New Limit
        </label>
        <input
          type="number"
          min={0}
          className="input-primary w-full"
          value={vleLimitInput}
          onChange={(event) => {
            setVleLimitInput(event.target.value);
          }}
        />
        <button
          type="button"
          onClick={handleVleSubmit}
          className="btn-primary w-full mt-4"
          disabled={isSavingVle}
        >
          {isSavingVle ? "Updating..." : "Update VLE Limit"}
        </button>
      </div>
    </div>
  );
}

export function LearnerMobileNumberUpdateContent() {
  const [oldMobileNumber, setOldMobileNumber] = useState("");
  const [newMobileNumber, setNewMobileNumber] = useState("");
  const [isSavingLearner, setIsSavingLearner] = useState(false);
  const mobileNumberRegex = /^[6-9]\d{9}$/;

  const handleLearnerMobileUpdate = async () => {
    const trimmedOld = oldMobileNumber.trim();
    const trimmedNew = newMobileNumber.trim();

    if (!mobileNumberRegex.test(trimmedOld)) {
      toast.error("Please enter a valid current mobile number.");
      return;
    }

    if (!mobileNumberRegex.test(trimmedNew)) {
      toast.error("Please enter a valid new mobile number.");
      return;
    }

    if (trimmedOld === trimmedNew) {
      toast.error("New mobile number must be different from the current number.");
      return;
    }

    setIsSavingLearner(true);
    try {
      await services.requestSvc.put(
        "/learner/mobile-number",
        {
          oldMobileNumber: trimmedOld,
          newMobileNumber: trimmedNew,
        },
        { withCredentials: true },
      );
      toast.success("Learner mobile number updated successfully.");
      setOldMobileNumber("");
      setNewMobileNumber("");
    } catch (error: any) {
      toast.error(error?.message || "Failed to update learner mobile number.");
    } finally {
      setIsSavingLearner(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h2 className="text-lg font-semibold mb-4">
        Update Learner Mobile Number
      </h2>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Current Mobile Number
      </label>
      <input
        type="text"
        inputMode="numeric"
        className="input-primary w-full"
        value={oldMobileNumber}
        onChange={(event) => setOldMobileNumber(event.target.value)}
        placeholder="Enter current mobile number"
      />

      <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
        New Mobile Number
      </label>
      <input
        type="text"
        inputMode="numeric"
        className="input-primary w-full"
        value={newMobileNumber}
        onChange={(event) => setNewMobileNumber(event.target.value)}
        placeholder="Enter new mobile number"
      />

      <button
        type="button"
        onClick={handleLearnerMobileUpdate}
        className="btn-primary w-full mt-4"
        disabled={isSavingLearner}
      >
        {isSavingLearner ? "Updating..." : "Update Mobile Number"}
      </button>
    </div>
  );
}
